import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { api, describeApiError, identityFieldsFor, type ConnectorDescriptorDto, type DiscoverItem, type TransformSystemPropertiesDto } from "../lib/api";
import { Button } from "../components/primitives/Button";
import { FormField, TextInput } from "../components/primitives/FormField";
import { Pulse } from "../components/primitives/Pulse";
import { SchemaForm } from "../components/SchemaForm";
import { EnrichmentsField } from "../components/schema-form/EnrichmentsField";
import { StateMappingField } from "../components/schema-form/StateMappingField";
import { FilterBuilder } from "../components/FilterBuilder";
import { IdentityPolicyEditor, stripUnmaskable, type DestinationIdentityPolicyDraft } from "../components/IdentityPolicyEditor";
import type { JsonSchema } from "../lib/schema-form/types";
import { isEnvCompatible } from "./pipeline-wizard-scoping";

// Edit an existing pipeline's mutable fields without a delete+recreate. Org and
// environment are immutable (the API omits them from PipelineUpdate), and
// source/dest/transform define the pipeline's identity — so this editor changes
// the things operators actually tune: the transform's params (e.g. a deal's
// HubSpot pipeline + stage) and the conflict policy. Everything else is sent back
// unchanged so the PUT is a faithful full-replace. (DEV-4812)

type Tier = "source-wins" | "dest-wins";

interface Transform {
  id: string;
  sourceObjectType?: string;
  destObjectType?: string;
  paramsJsonSchema: JsonSchema | null;
  unmaskableKeys?: string[];
  systemProperties?: TransformSystemPropertiesDto | null;
}

interface LoadedPipeline {
  name: string;
  sourceInstanceId: string;
  destInstanceId: string;
  transformId: string;
  transformParams: Record<string, unknown>;
  triggers: Array<Record<string, unknown>>;
  conflictPolicy: { defaultTier: Tier; overrides?: unknown[] };
  /** Null means today's behavior: every record sent mints a new one. */
  identityPolicy: DestinationIdentityPolicyDraft | null;
  filter: Record<string, unknown>;
  enabled: boolean;
}

// Pure, side-effect-free form — takes the loaded pipeline + the transform schema,
// renders the editable fields, and hands a complete PipelineUpdate body to onSave.
export function PipelineEditForm({
  pipeline,
  paramsSchema,
  sourceObjectType,
  destObjectType,
  connections = [],
  identityFields = [],
  unmaskableKeys = [],
  destProperties = [],
  systemProperties,
  stages,
  onSave,
  onCancel,
  error,
  saving,
}: {
  pipeline: LoadedPipeline;
  paramsSchema: JsonSchema | null;
  sourceObjectType?: string;
  destObjectType?: string;
  connections?: { id: string; name: string; connectorType: string; environment?: string | null }[];
  identityFields?: string[];
  unmaskableKeys?: string[];
  destProperties?: { id: string; name: string }[];
  systemProperties?: TransformSystemPropertiesDto | null;
  stages?: DiscoverItem[];
  onSave: (body: Omit<LoadedPipeline, never>) => void;
  onCancel: () => void;
  error?: string | null;
  saving?: boolean;
}) {
  const [name, setName] = useState(pipeline.name);
  const [params, setParams] = useState<Record<string, unknown>>(pipeline.transformParams ?? {});
  const [tier, setTier] = useState<Tier>(pipeline.conflictPolicy?.defaultTier ?? "source-wins");
  const [filter, setFilter] = useState<Record<string, unknown>>(pipeline.filter ?? {});
  const [identityPolicy, setIdentityPolicy] = useState<DestinationIdentityPolicyDraft | null>(
    pipeline.identityPolicy ?? null,
  );

  const widgetContext = {
    connections,
    // `api.discover` is a stable module-level reference — pass it directly rather
    // than a fresh closure, so DiscoverInput's fetch effect (keyed on `discover`)
    // doesn't re-fire on every keystroke/re-render.
    discover: api.discover,
    stateMapping: {
      stateField: systemProperties?.stateField,
      canArchive: systemProperties?.canArchive,
      destObjectType,
      stages: stages ?? [],
      defaultStateStages: systemProperties?.defaultStateStages,
      // Spec §10/§11: fold an existing legacy stage map into the first mapping
      // the editor writes, so enabling reproduces what the pipeline already does.
      legacyStageMap: (pipeline.transformParams as { dealStageByStatus?: Record<string, string> })
        ?.dealStageByStatus,
      // Read from the live editor state, not the saved pipeline: adoption is
      // configured in this same form, so the caution appears as soon as the
      // operator turns it on.
      adoptionEnabled: identityPolicy != null,
    },
  };

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave({
      ...pipeline,
      name,
      transformParams: params,
      conflictPolicy: { ...pipeline.conflictPolicy, defaultTier: tier },
      // A mask saved before a transform declared one of these still names it,
      // and its checkbox is disabled — so nothing the operator can click here
      // removes it, and every save would 422. Normalize on the way out.
      identityPolicy: stripUnmaskable(identityPolicy, unmaskableKeys),
      filter,
    });
  }

  return (
    <form onSubmit={submit} style={{ maxWidth: 720 }}>
      <FormField label="NAME *" hint="Operator-readable name.">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
      </FormField>

      <FormField label="CONFLICT POLICY *" hint="On conflict, which side wins.">
        <select className="field__input" value={tier} onChange={(e) => setTier(e.target.value as Tier)}>
          <option value="source-wins">SOURCE-WINS</option>
          <option value="dest-wins">DEST-WINS</option>
        </select>
      </FormField>

      <div className="uppercase-label" style={{ margin: "24px 0 8px" }}>TRANSFORM PARAMS</div>
      <div className="page-help" style={{ marginTop: 0 }}>
        {pipeline.transformId}
      </div>
      {paramsSchema ? (
        <SchemaForm
          schema={paramsSchema}
          value={params}
          onChange={(p) => setParams(p as Record<string, unknown>)}
          widgets={{ enrichments: EnrichmentsField, stateMapping: StateMappingField }}
          widgetContext={widgetContext}
        />
      ) : (
        <p className="page-help">This transform has no configurable params.</p>
      )}

      <div className="uppercase-label" style={{ margin: "24px 0 8px" }}>RECORD ADOPTION</div>
      <IdentityPolicyEditor
        value={identityPolicy}
        onChange={setIdentityPolicy}
        destObjectType={destObjectType}
        identityFields={identityFields}
        connections={connections}
        discover={api.discover}
        destProperties={destProperties}
        unmaskableKeys={unmaskableKeys}
        // Spec §14: a pipeline whose SAVED policy is non-null may already have
        // written to records the customer created, and the API refuses to
        // remove it. Read from the loaded pipeline, not the live editor state —
        // this is about what is persisted, not about what the operator has
        // typed since.
        savedPolicyExists={pipeline.identityPolicy != null}
      />

      <div className="uppercase-label" style={{ margin: "24px 0 8px" }}>FILTER</div>
      <FilterBuilder value={filter} onChange={setFilter} sourceObjectType={sourceObjectType} destObjectType={destObjectType} />

      {error && <div className="field__error" style={{ marginTop: 12 }}>{error}</div>}
      <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
        <Button type="submit" variant="primary" disabled={saving}>{saving ? "SAVING…" : "SAVE"}</Button>
        <Button type="button" variant="secondary" onClick={onCancel}>CANCEL</Button>
      </div>
    </form>
  );
}

// ─── Live page: load pipeline + transform schema, save via PUT ────────────────

export function PipelineEditor() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [pipeline, setPipeline] = useState<LoadedPipeline | null>(null);
  const [paramsSchema, setParamsSchema] = useState<JsonSchema | null>(null);
  const [sourceObjectType, setSourceObjectType] = useState<string | undefined>(undefined);
  const [destObjectType, setDestObjectType] = useState<string | undefined>(undefined);
  const [connections, setConnections] = useState<{ id: string; name: string; connectorType: string; environment?: string | null }[]>([]);
  const [identityFields, setIdentityFields] = useState<string[]>([]);
  const [unmaskableKeys, setUnmaskableKeys] = useState<string[]>([]);
  const [destProperties, setDestProperties] = useState<{ id: string; name: string }[]>([]);
  const [systemProperties, setSystemProperties] = useState<TransformSystemPropertiesDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, tRes, iRes, cRes] = await Promise.all([
          api.getPipeline(id),
          api.listTransforms(),
          api.listConnectorInstances(),
          api.listConnectors(),
        ]);
        if (cancelled) return;
        const p = pRes.pipeline;
        // Only same-org, env-compatible connections: a cross-org connection is
        // denied at runtime by the worker's tenant-isolation guard (DEV-6380),
        // and a cross-env one is rejected at save (DEV-6562) — either fails
        // every event, so don't offer it here. A `null` connection environment
        // means any-env, always compatible with the pipeline's environment.
        setConnections((iRes.instances ?? [])
          .filter((i: { organizationId: string; environment?: string | null }) =>
            i.organizationId === p.organizationId && isEnvCompatible(i.environment, p.environment))
          .map((i: { id: string; name: string; connectorType: string; environment?: string | null }) =>
            ({ id: i.id, name: i.name, connectorType: i.connectorType, environment: i.environment })));
        const loaded: LoadedPipeline = {
          name: p.name,
          // tolerate either the flat id or the embedded instance projection
          sourceInstanceId: p.sourceInstanceId ?? p.sourceInstance?.id,
          destInstanceId: p.destInstanceId ?? p.destInstance?.id,
          transformId: p.transformId,
          transformParams: p.transformParams ?? {},
          triggers: p.triggers ?? [{ mode: "sqs" }],
          conflictPolicy: p.conflictPolicy ?? { defaultTier: "source-wins" },
          identityPolicy: (p.identityPolicy ?? null) as DestinationIdentityPolicyDraft | null,
          filter: p.filter ?? {},
          enabled: p.enabled ?? true,
        };
        setPipeline(loaded);
        const t = (tRes.transforms as Transform[]).find((x) => x.id === loaded.transformId);
        setParamsSchema(t?.paramsJsonSchema ?? null);
        setSourceObjectType(t?.sourceObjectType);
        setDestObjectType(t?.destObjectType);
        setSystemProperties(t?.systemProperties ?? null);

        // Record adoption (spec §6.5, §6.8): which properties may be matched on,
        // which may never be masked, and what the destination actually offers.
        const destConnectorType = ((iRes.instances ?? []) as { id: string; connectorType: string }[])
          .find((i) => i.id === loaded.destInstanceId)?.connectorType;
        setIdentityFields(identityFieldsFor(
          (cRes.connectors ?? []) as ConnectorDescriptorDto[],
          destConnectorType,
          t?.destObjectType,
        ));
        // §6.5: the MASK list, not the mapping list — it also carries the
        // source-time property, which the save-time validator rejects in a mask.
        setUnmaskableKeys(t?.unmaskableKeys ?? []);
        if (loaded.destInstanceId && t?.destObjectType) {
          // The discover kind takes the SHORT object name (`properties:deal`),
          // matching what the save-time check asks the connector for.
          const shortType = t.destObjectType.slice(t.destObjectType.indexOf(".") + 1);
          // A destination we can't reach must not block the section — the API
          // validates the mask on save regardless.
          const dRes = await api
            .discover(loaded.destInstanceId, `properties:${shortType}`)
            .catch(() => ({ items: [] }));
          if (!cancelled) setDestProperties(dRes.items ?? []);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const [stages, setStages] = useState<DiscoverItem[]>([]);

  // The stage picker's options. Only fetched for a transform that actually has a
  // stage to set, and best-effort: on a failure the widget degrades to free text
  // and the save-time check warns.
  //
  // Clears FIRST, on every run and before every early return. Options from a
  // previously selected portal are worse than no options: the picker would offer
  // stage ids that do not exist in the portal now selected, and the operator has
  // no way to tell. `cancelled` additionally drops a slow response that lands
  // after the selection moved on.
  useEffect(() => {
    setStages([]);
    const instanceId = pipeline?.destInstanceId;
    if (!instanceId || !systemProperties?.stateField) return;
    let cancelled = false;
    api.discover(instanceId, "dealPipelines")
      .then((r) => { if (!cancelled) setStages(r.items ?? []); })
      .catch(() => { if (!cancelled) setStages([]); });
    return () => { cancelled = true; };
  }, [pipeline?.destInstanceId, systemProperties?.stateField]);

  if (loadError) return <p className="field__error">Couldn't load pipeline: {loadError}</p>;
  if (!pipeline) return <Pulse />;

  async function save(body: LoadedPipeline) {
    setSaveError(null);
    setWarnings([]);
    setSaving(true);
    try {
      const res = await api.updatePipeline(id, body);
      // Warnings mean the save landed but a check couldn't run. Stay put and say
      // so — navigating away would throw them out unread. The page stays live
      // afterwards, so the loaded pipeline has to match what was just persisted
      // too — otherwise the next edit (e.g. turning record adoption off) is
      // computed against a stale row and can send a policy shape the API no
      // longer accepts.
      if (res?.warnings?.length) {
        setWarnings(res.warnings);
        if (res.pipeline) setPipeline(res.pipeline);
      } else nav(`/pipelines/${id}`);
    } catch (e) {
      // A 422 says "Pipeline validation failed" and names the offending
      // property only in `issues`. Show both — the bare message sent operators
      // hunting through every checkbox on the screen.
      setSaveError(describeApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <Link to={`/pipelines/${id}`} className="uppercase-label">← PIPELINE</Link>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 22, margin: "8px 0 16px" }}>Edit pipeline</div>
      {warnings.length > 0 && (
        <div className="save-notice" role="status">
          <div className="save-notice__head">Saved. Some checks couldn't run — the pipeline will still try them when it runs.</div>
          <ul className="save-notice__list">
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}
      <PipelineEditForm
        pipeline={pipeline}
        paramsSchema={paramsSchema}
        sourceObjectType={sourceObjectType}
        destObjectType={destObjectType}
        connections={connections}
        identityFields={identityFields}
        unmaskableKeys={unmaskableKeys}
        destProperties={destProperties}
        systemProperties={systemProperties}
        stages={stages}
        onSave={(b) => void save(b)}
        onCancel={() => nav(`/pipelines/${id}`)}
        error={saveError}
        saving={saving}
      />
    </div>
  );
}
